import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useModuleSearch } from '@/shared/components/search/ModuleSearchContext';
import {
  Button,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  OperatorAvatarFilter,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
  type OperatorAvatarFilterOperator,
} from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { LeadListRow } from '../components/LeadListRow';
import { LeadGridCard } from '../components/LeadGridCard';
import { LeadsAdvancedFiltersComponent } from '../components/LeadsAdvancedFilters';
import { BulkChangeFunnelModal } from '../components/BulkChangeFunnelModal';
import { LeadCardAction } from '../components/LeadCard';
import { LeadFormDrawer } from '../components/LeadFormDrawer';
import { DeleteLeadDialog } from '../components/DeleteLeadDialog';
import { LeadDetailsDialog } from '../components/LeadDetailsDialog';
import { TransferLeadDialog } from '../components/TransferLeadDialog';
import { WinLeadDialog, type WinLeadData } from '../components/WinLeadDialog';
import { LoseLeadDialog, type LoseLeadData } from '../components/LoseLeadDialog';
import { AlertSnoozeDialog } from '../components/AlertSnoozeDialog';
import { compareLeads, type LeadSortKey } from '../components/leadRowHelpers';
import { PopulatedLead, LeadsAdvancedFilters as AdvancedFiltersType } from '../types';
import { useLeadOperatorsDropdown, useLeads, useLeadsFilters, useTransferLead } from '../hooks';
import { useBulkLeadOperations, type BulkOperationPayload } from '../hooks/useBulkLeadOperations';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useClearStaleOperator } from '@/shared/hooks/useClearStaleOperator';
import { toOperatorFilterOperator } from '@/shared/utils/contextOperators';
import { useFunnelsDropdown } from '@/modules/funnels';
import { leadsService } from '../services/leadsService';
import { getEffectiveScopeForFunnels, mapScopeToDropdownFlag } from '../utils/leadsAdapter';

const STORAGE_KEY = 'crm_leads_list_filters';
const VIEWMODE_KEY = 'crm_leads_list_viewmode';
const DEFAULT_LIMIT = 25;

const LEADS_LIST_DEFAULT_FILTERS: AdvancedFiltersType = {
  businessStatus: ['pending'],
};

type ViewMode = 'table' | 'grid';

export const LeadsListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // Scope (cross-funnel) — vem de `funnels-users.scope` per-funil hidratado
  // pelo backend em `funnel.userScope`. Regra D-context-gate (CLAUDE.md NUNCA #47).
  // Pega o scope mais permissivo entre os funis acessíveis: 'all' > 'team' > 'own'.
  const { data: funnelsData = [] } = useFunnelsDropdown();
  const userLeadsScope = useMemo(
    () => getEffectiveScopeForFunnels(funnelsData),
    [funnelsData],
  );
  const showOperatorFilter = userLeadsScope !== 'own';
  const operatorsDropdownScope = mapScopeToDropdownFlag(userLeadsScope);

  // State
  const [searchTerm, setSearchTerm] = useState('');
  useModuleSearch({ placeholder: 'Buscar leads…', value: searchTerm, onChange: setSearchTerm });
  const [page, setPage] = useState(1);
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /**
   * Seleção que atravessa as páginas: "todos os leads que casam com os filtros
   * atuais", e não a lista de ids marcada. Quando ligada, o lote NÃO manda
   * ids — manda os filtros, e quem resolve quem são é o servidor.
   *
   * Por que não simplesmente buscar todos os ids e marcar: a listagem pagina
   * de 25 em 25 e o filtro pode casar dezenas de milhares de leads; além do
   * tráfego, a lista já estaria velha no instante do clique (lead novo entra,
   * lead muda de etapa). Mandar o filtro faz o lote e a tela concordarem.
   */
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [isBulkFunnelOpen, setIsBulkFunnelOpen] = useState(false);

  // "+" central do bottom-nav mobile cria lead nesta tela (mockup `.m-fab`).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); setIsCreateLeadOpen(true); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);
  const [sort, setSort] = useState<{ key: LeadSortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      return localStorage.getItem(VIEWMODE_KEY) === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(VIEWMODE_KEY, viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Dialog states
  const [selectedLead, setSelectedLead] = useState<PopulatedLead | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isWinOpen, setIsWinOpen] = useState(false);
  const [isLoseOpen, setIsLoseOpen] = useState(false);
  const [isSnoozeOpen, setIsSnoozeOpen] = useState(false);

  // Filters
  const {
    advancedFilters,
    appliedFilters,
    selectedOperatorIds,
    activeFiltersCount,
    handleAdvancedFiltersChange,
    handleOperatorSelectionChange,
    handleApplyFilters,
    toLeadQuery,
  } = useLeadsFilters();

  // Initialize filters from localStorage (or use page defaults)
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        handleAdvancedFiltersChange(parsed);
        handleApplyFilters(parsed);
      } catch {
        handleAdvancedFiltersChange(LEADS_LIST_DEFAULT_FILTERS);
        handleApplyFilters(LEADS_LIST_DEFAULT_FILTERS);
      }
    } else {
      handleAdvancedFiltersChange(LEADS_LIST_DEFAULT_FILTERS);
      handleApplyFilters(LEADS_LIST_DEFAULT_FILTERS);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, []);

  // Wrapper: apply + persist filters
  const handleFiltersChange = useCallback((filters: AdvancedFiltersType) => {
    handleAdvancedFiltersChange(filters);
    handleApplyFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    setPage(1);
    clearSelection();
  }, [handleAdvancedFiltersChange, handleApplyFilters, clearSelection]);

  // Build query — NO funnelId (all leads)
  const leadQuery = toLeadQuery({
    page,
    limit: DEFAULT_LIMIT,
    search: searchTerm.trim() || undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: leadsData, isLoading, refetch } = useLeads(leadQuery);
  const transferLeadMutation = useTransferLead();

  // Operadores do filtro: com funil escolhido nos filtros avançados, só quem
  // participa dele(s) + quem tem lead atribuído nele(s); sem funil, todos.
  const filteredFunnelIds = useMemo(() => {
    const { funnelId } = appliedFilters;
    if (!funnelId) return [];
    return Array.isArray(funnelId) ? funnelId : [funnelId];
  }, [appliedFilters]);
  const hasFunnelFilter = filteredFunnelIds.length > 0;
  const { data: usersData } = useUsersDropdown({
    status: 'active',
    ...(operatorsDropdownScope ? { scope: operatorsDropdownScope } : {}),
    enabled: showOperatorFilter && !hasFunnelFilter,
  });
  const { data: funnelOperatorsData } = useLeadOperatorsDropdown({
    funnelIds: filteredFunnelIds,
    ...(operatorsDropdownScope ? { scope: operatorsDropdownScope } : {}),
    enabled: showOperatorFilter && hasFunnelFilter,
  });
  // `undefined` = carregando (o useClearStaleOperator não age).
  const loadedOperators: OperatorAvatarFilterOperator[] | undefined = useMemo(() => {
    if (hasFunnelFilter) return funnelOperatorsData?.map(toOperatorFilterOperator);
    return usersData?.map(u => ({ id: u.id, name: u.name, avatar: u.avatar }));
  }, [hasFunnelFilter, funnelOperatorsData, usersData]);
  const operators = loadedOperators ?? [];

  // Leads data
  const leads = useMemo(() => leadsData?.items ?? [], [leadsData]);
  const total = leadsData?.total ?? 0;
  const totalPages = leadsData?.totalPages || 1;

  // Sort client-side na página carregada (server já entrega createdAt desc; o
  // sort abaixo reordena a página atual pela coluna escolhida).
  const sortedLeads = useMemo(() => {
    const copy = [...leads];
    copy.sort((a, b) => {
      const cmp = compareLeads(a, b, sort.key);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [leads, sort]);

  // Reset page on search
  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [searchTerm, clearSelection]);

  // Selection
  // Com a seleção global ligada, o contador é o `total` da listagem — o MESMO
  // número do chip do cabeçalho, produzido pela mesma query (mesmos filtros,
  // mesma regra de visibilidade). É por isso que não existe endpoint separado
  // de contagem: ele devolveria este mesmo número.
  const selectedCount = selectAllMatching ? total : selectedIds.size;
  const allOnPageSelected = leads.length > 0 && leads.every((l) => selectedIds.has(l.id));
  const someOnPageSelected = leads.some((l) => selectedIds.has(l.id));
  /** Há mais leads fora da página do que os marcados nela. */
  const canSelectAllMatching = !selectAllMatching && allOnPageSelected && total > leads.length;

  /** Como o lote escolhe os leads — os filtros aplicados, ou os ids marcados. */
  const bulkSelection = useMemo(() => (
    selectAllMatching
      ? {
        selection: {
          ...(leadQuery.filters ? { filters: leadQuery.filters } : {}),
          ...(leadQuery.search ? { search: leadQuery.search } : {}),
        },
      }
      : { leadIds: Array.from(selectedIds) }
  ), [selectAllMatching, leadQuery.filters, leadQuery.search, selectedIds]);

  // Mexer nos quadradinhos volta a seleção a ser "o que está marcado": manter
  // a seleção global ligada depois de desmarcar uma linha mentiria sobre o
  // alcance do próximo lote.
  const toggleSelect = useCallback((id: string) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (leads.every((l) => next.has(l.id))) leads.forEach((l) => next.delete(l.id));
      else leads.forEach((l) => next.add(l.id));
      return next;
    });
  }, [leads]);

  const handleSort = useCallback((key: LeadSortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
    clearSelection();
  }, [clearSelection]);

  const handleOperatorChange = useCallback((userId: string | null) => {
    handleOperatorSelectionChange(userId === null ? [] : [userId]);
    setPage(1);
    clearSelection();
  }, [handleOperatorSelectionChange, clearSelection]);

  // Mudou o funil do filtro e o operador escolhido não está nele → limpa.
  useClearStaleOperator(selectedOperatorIds[0] ?? null, loadedOperators, () => handleOperatorChange(null));

  // ── Ações em massa ──
  // Uma chamada só (`POST /leads/bulk`), nos dois modos de seleção. O laço de
  // mutations unitárias que existia aqui não sobreviveria a "selecionar
  // todos": 5 mil leads viravam 5 mil requisições.
  const bulkMutation = useBulkLeadOperations();

  const runBulk = useCallback((
    payload: Omit<BulkOperationPayload, 'leadIds' | 'selection'>,
    successMessage: (affected: number) => string,
  ) => {
    bulkMutation.mutate(
      { ...bulkSelection, ...payload } as BulkOperationPayload,
      {
        onSuccess: ({ affected }) => {
          clearSelection();
          // Um lote pode esvaziar a página atual (exclusão, ou mudança de
          // funil com filtro de funil ativo) e deixar o usuário olhando para
          // uma página que não existe mais.
          setPage(1);
          toast.success(successMessage(affected));
        },
        onError: () => toast.error('Não foi possível concluir a ação em massa. Tente novamente.'),
        // O modal fecha quando a chamada TERMINA, não quando o botão é
        // clicado: um lote grande leva segundos, e fechar antes deixaria o
        // usuário sem sinal nenhum de que algo está acontecendo.
        onSettled: () => setIsBulkFunnelOpen(false),
      },
    );
  }, [bulkMutation, bulkSelection, clearSelection]);

  const bulkDelete = useCallback(async () => {
    if (selectedCount === 0) return;
    if (!(await confirm({
      message: selectAllMatching
        ? `Excluir TODOS os ${selectedCount} lead(s) que correspondem aos filtros atuais?`
        : `Excluir ${selectedCount} lead(s) selecionado(s)?`,
      title: 'Confirmar exclusão',
      confirmButtonText: 'Excluir',
      variant: 'danger',
    }))) return;
    runBulk({ operation: 'delete' }, (affected) => `${affected} lead(s) excluído(s).`);
  }, [selectedCount, selectAllMatching, confirm, runBulk]);

  const bulkChangeFunnel = useCallback((funnelId: string, stepId: string) => {
    runBulk(
      { operation: 'changeFunnel', data: { funnelId, stepId } },
      (affected) => `${affected} lead(s) movido(s).`,
    );
  }, [runBulk]);

  // Qualify/disqualify handlers
  const handleQualifyLead = useCallback(async (lead: PopulatedLead) => {
    try {
      await leadsService.updateLead(lead.id, { qualifyStatus: 'qualified' });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead qualificado com sucesso!');
    } catch {
      toast.error('Erro ao qualificar lead. Tente novamente.');
    }
  }, [queryClient]);

  const handleDisqualifyLead = useCallback(async (lead: PopulatedLead) => {
    try {
      await leadsService.updateLead(lead.id, { qualifyStatus: 'disqualified' });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead desqualificado com sucesso!');
    } catch {
      toast.error('Erro ao desqualificar lead. Tente novamente.');
    }
  }, [queryClient]);

  // Reativar alertas (unsnooze direto, sem dialog)
  const handleUnsnoozeLead = useCallback(async (lead: PopulatedLead) => {
    try {
      await leadsService.snoozeAlert(lead.id, null);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Alertas reativados.');
    } catch {
      toast.error('Erro ao reativar alertas. Tente novamente.');
    }
  }, [queryClient]);

  // Confirmação do AlertSnoozeDialog
  const handleSnoozeConfirm = async (until: string) => {
    if (!selectedLead) return;
    try {
      await leadsService.snoozeAlert(selectedLead.id, until);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Alerta silenciado.');
      setIsSnoozeOpen(false);
      setSelectedLead(null);
    } catch {
      toast.error('Erro ao silenciar alerta. Tente novamente.');
    }
  };

  // Lead action handler
  const handleLeadAction = useCallback((action: LeadCardAction, lead: PopulatedLead) => {
    if (action === 'unsnooze') {
      handleUnsnoozeLead(lead);
      return;
    }
    setSelectedLead(lead);
    switch (action) {
      case 'view': setIsDetailsOpen(true); break;
      case 'edit': setIsEditOpen(true); break;
      case 'transfer': setIsTransferOpen(true); break;
      case 'delete': setIsDeleteOpen(true); break;
      case 'win': setIsWinOpen(true); break;
      case 'lose': setIsLoseOpen(true); break;
      case 'qualify': handleQualifyLead(lead); break;
      case 'disqualify': handleDisqualifyLead(lead); break;
      case 'snooze': setIsSnoozeOpen(true); break;
    }
  }, [handleQualifyLead, handleDisqualifyLead, handleUnsnoozeLead]);

  const handleTransferLead = async (
    transferType: 'assignee' | 'funnel',
    data: { newAssigneeId?: string; newFunnelId?: string; newStepId?: string }
  ) => {
    if (!selectedLead) return;
    try {
      if (transferType === 'assignee') {
        await transferLeadMutation.mutateAsync({
          leadId: selectedLead.id,
          body: { toUserId: data.newAssigneeId ?? null },
        });
      } else {
        await leadsService.updateLead(selectedLead.id, {
          funnelId: data.newFunnelId!,
          stepId: data.newStepId!,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      setIsTransferOpen(false);
      setSelectedLead(null);
      toast.success('Lead transferido com sucesso!');
    } catch {
      toast.error('Erro ao transferir lead. Tente novamente.');
    }
  };

  const handleWinLead = async (data: WinLeadData) => {
    if (!selectedLead) return;
    try {
      await leadsService.updateLead(selectedLead.id, data);
      refetch();
      setIsWinOpen(false);
      setSelectedLead(null);
      toast.success('Lead marcado como ganho!');
    } catch {
      toast.error('Erro ao marcar lead como ganho.');
    }
  };

  const handleLoseLead = async (data: LoseLeadData) => {
    if (!selectedLead) return;
    try {
      await leadsService.updateLead(selectedLead.id, data);
      refetch();
      setIsLoseOpen(false);
      setSelectedLead(null);
      toast.success('Lead marcado como perdido!');
    } catch {
      toast.error('Erro ao marcar lead como perdido.');
    }
  };

  const handleDialogSuccess = () => {
    refetch();
  };

  const showFilterRow = activeFiltersCount > 0;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        chain="cl-tb"
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>CRM</span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Leads</span>
          </nav>
          {total > 0 && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="leads-count"
            >
              {total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por nome, telefone ou e-mail…"
            data-testid="leads-search"
          />
        }
        right={
          <>
            {/* Direita — ordem canônica (F15): Operadores → Filtros → Toggle → sep → CTA */}
            {showOperatorFilter && (
              <OperatorAvatarFilter
                operators={operators}
                selectedId={selectedOperatorIds[0] || null}
                onSelect={handleOperatorChange}
                loading={isLoading}
                data-testid="leads-operators"
              />
            )}

            {/* Filtros — variant 'toolbar' abre o popover direto (regra travada: filtros SÓ via botão Filtros) */}
            <LeadsAdvancedFiltersComponent
              variant="toolbar"
              filters={advancedFilters}
              onFiltersChange={handleFiltersChange}
            />

            {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
            <ViewToggle
              modes={[
                { id: 'table', icon: 'list', label: 'Lista' },
                { id: 'grid', icon: 'grid', label: 'Cards' },
              ]}
              value={viewMode}
              onChange={setViewMode}
              data-testid="leads-view"
            />

            {/* .tb-sep antes do CTA */}
            <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

            {/* Novo lead (único primário) */}
            <Button
              variant="primary"
              icon="plus"
              collapsibleLabel
              onClick={() => setIsCreateLeadOpen(true)}
              data-testid="new-lead"
            >
              Novo lead
            </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + chip Operadores + Filtros (variant toolbar, popover próprio) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar lead…"
              data-testid="leads-search-mobile"
            />
            {showOperatorFilter && (
              <OperatorAvatarFilter
                operators={operators}
                selectedId={selectedOperatorIds[0] || null}
                onSelect={handleOperatorChange}
                loading={isLoading}
                data-testid="leads-operators-mobile"
              />
            )}
            <span className="flex-none">
              <LeadsAdvancedFiltersComponent
                variant="toolbar"
                filters={advancedFilters}
                onFiltersChange={handleFiltersChange}
              />
            </span>
          </>
        }
      />

      {/* ── Filterbar — SÓ chips lilás + "Limpar tudo" (DS 3.3); some quando vazia ── */}
      {showFilterRow && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2 overflow-x-auto"
          style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
          data-testid="leads-filterbar"
        >
          <LeadsAdvancedFiltersComponent variant="chips" filters={advancedFilters} onFiltersChange={handleFiltersChange} />
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2 flex-wrap" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-xs" style={{ color: 'var(--theme-primary)' }} data-testid="leads-bulk-count">
            {selectAllMatching
              ? `Todos os ${selectedCount} leads do filtro selecionados`
              : `${selectedCount} selecionado(s)`}
          </span>

          {/* Ponte da página para o filtro inteiro: só aparece quando a página
              está toda marcada E existe lead além dela — é o único momento em
              que "selecionar todos" acrescenta alguma coisa. */}
          {canSelectAllMatching && (
            <button
              onClick={() => setSelectAllMatching(true)}
              className="text-xs font-semibold underline underline-offset-2"
              style={{ color: 'var(--theme-primary)' }}
              data-testid="leads-bulk-select-all-matching"
            >
              Selecionar todos os {total} que correspondem aos filtros
            </button>
          )}

          <span className="flex-1" />

          <button
            onClick={() => setIsBulkFunnelOpen(true)}
            disabled={bulkMutation.isPending}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
            data-testid="leads-bulk-change-funnel"
          >
            <Icon name="layers" className="w-3.5 h-3.5" /> Funil e etapa
          </button>
          <button
            onClick={bulkDelete}
            disabled={bulkMutation.isPending}
            className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
            data-testid="leads-bulk-delete"
          >
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="leads-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {isLoading && <ListPageLoading />}

          {!isLoading && leads.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum lead encontrado"
              description={searchTerm || activeFiltersCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie o primeiro lead para começar'}
              actionLabel="Novo lead"
              onAction={() => setIsCreateLeadOpen(true)}
            />
          )}

          {/* Grid (Cards) — todas as larguras */}
          {!isLoading && leads.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedLeads.map((lead) => (
                <LeadGridCard
                  key={lead.id}
                  lead={lead}
                  selected={selectedIds.has(lead.id)}
                  onAction={handleLeadAction}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && leads.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedLeads.map((lead) => (
                  <LeadGridCard
                    key={lead.id}
                    lead={lead}
                    selected={selectedIds.has(lead.id)}
                    onAction={handleLeadAction}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" style={{ minWidth: 1280 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                        <SelectAllHeadCell
                          checked={allOnPageSelected}
                          indeterminate={someOnPageSelected && !allOnPageSelected}
                          onToggle={toggleSelectAllOnPage}
                          data-testid="leads-select-all"
                        />
                        <SortableHeadCell label="Data" sortKey="date" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-date" />
                        <SortableHeadCell label="Nome" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-name" />
                        <PlainHeadCell label="Canal" />
                        <SortableHeadCell label="Funil" sortKey="funnel" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-funnel" />
                        <PlainHeadCell label="Etapa" />
                        <SortableHeadCell label="Valor" sortKey="value" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-value" />
                        <PlainHeadCell label="Responsável" />
                        <SortableHeadCell label="Prioridade" sortKey="priority" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-priority" />
                        <PlainHeadCell label="Atividade" />
                        <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-status" />
                        <SortableHeadCell label="Qualificação" sortKey="qualify" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="leads-sort-qualify" />
                        <PlainHeadCell className="w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLeads.map((lead) => (
                        <LeadListRow
                          key={lead.id}
                          lead={lead}
                          selected={selectedIds.has(lead.id)}
                          onToggleSelect={toggleSelect}
                          onAction={handleLeadAction}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Paginação (mockup: sempre visível com "Mostrando X–Y de N leads") */}
          {!isLoading && leads.length > 0 && (
            <ListPagePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={total}
              pageSize={DEFAULT_LIMIT}
              itemLabel="leads"
            />
          )}
        </div>
      </div>

      {/* Dialogs */}
      <LeadFormDrawer
        isOpen={isCreateLeadOpen}
        onClose={() => setIsCreateLeadOpen(false)}
        mode="create"
        onSuccess={() => { refetch(); setIsCreateLeadOpen(false); }}
      />

      <LeadDetailsDialog
        isOpen={isDetailsOpen}
        onClose={() => { setIsDetailsOpen(false); setSelectedLead(null); }}
        lead={selectedLead}
        onSuccess={handleDialogSuccess}
      />

      <LeadFormDrawer
        isOpen={isEditOpen}
        onClose={() => { setIsEditOpen(false); setSelectedLead(null); }}
        mode="edit"
        lead={selectedLead}
        onSuccess={() => { refetch(); setIsEditOpen(false); setSelectedLead(null); }}
      />

      <DeleteLeadDialog
        isOpen={isDeleteOpen}
        onClose={() => { setIsDeleteOpen(false); setSelectedLead(null); }}
        lead={selectedLead}
        onSuccess={handleDialogSuccess}
      />

      {isTransferOpen && selectedLead && (
        <TransferLeadDialog
          lead={selectedLead}
          onClose={() => { setIsTransferOpen(false); setSelectedLead(null); }}
          onTransfer={handleTransferLead}
        />
      )}

      <WinLeadDialog
        isOpen={isWinOpen}
        onClose={() => { setIsWinOpen(false); setSelectedLead(null); }}
        lead={selectedLead}
        onConfirm={handleWinLead}
      />

      <LoseLeadDialog
        isOpen={isLoseOpen}
        onClose={() => { setIsLoseOpen(false); setSelectedLead(null); }}
        lead={selectedLead}
        onConfirm={handleLoseLead}
      />

      <AlertSnoozeDialog
        isOpen={isSnoozeOpen}
        lead={selectedLead}
        onClose={() => { setIsSnoozeOpen(false); setSelectedLead(null); }}
        onConfirm={handleSnoozeConfirm}
      />

      <BulkChangeFunnelModal
        isOpen={isBulkFunnelOpen}
        onClose={() => setIsBulkFunnelOpen(false)}
        affectedCount={selectedCount}
        onConfirm={bulkChangeFunnel}
        isPending={bulkMutation.isPending}
      />
    </div>
  );
};
